import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Info } from "lucide-react";

const FeaturedWork = ({ language, images }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [showInfoBox, setShowInfoBox] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setShowInfoBox(entry.isIntersecting),
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={sectionRef}
            className="min-h-64 w-full bg-gradient-to-br from-[#7f5af0] via-[#9f8df1] to-[#ffffff] text-black flex flex-col items-center pt-16 pb-10">
            {/* Image Container */}
            <div className="relative flex flex-col md:flex-row items-center">
                {/* Desktop Layout */}
                <div className="relative hidden md:flex justify-center items-center h-[600px]">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className={`absolute cursor-pointer transition-transform hover:scale-105
                                ${index === 0 ? "top-40 left-[calc(50%-525px)] w-[400px] h-[250px] z-10" : ""}
                                ${index === 1 ? "top-6 left-[50%] translate-x-[-50%] w-96 h-[500px] z-0" : ""}
                                ${index === 2 ? "top-48 left-[calc(50%+175px)] w-64 h-64 z-10" : ""}`}
                            onClick={() => setSelectedImage(image)}
                        >
                            <img
                                src={image.src}
                                alt={image.title}
                                className="w-full h-full object-cover shadow-xl border border-gray-300 rounded-lg"
                            />
                        </div>
                    ))}
                </div>

                {/* Mobile Layout */}
                <div className="flex flex-col md:hidden w-full space-y-6">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className="w-full px-4 cursor-pointer"
                            onClick={() => setSelectedImage(image)}
                        >
                            <img
                                src={image.src}
                                alt={image.title}
                                className="w-full h-auto object-cover shadow-lg border border-gray-300 rounded-lg"
                            />
                            <div className="mt-4 text-center">
                                <h2 className="text-xl font-semibold">{image.title}</h2>
                                <p className="text-sm text-gray-700">{image.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Centered Paragraph Below Images */}
            <div className="max-w-3xl px-4 text-center text-lg text-gray-700 mt-5">
                <h2 className="text-2xl font-semibold pb-4">Hodgies Art</h2>
                <p>
                    {language === "English"
                        ? "Discover a variety of artistic creations, each with its own unique story and visual appeal. Explore the gallery and find inspiration in every piece of art."
                        : "Descubre una variedad de creaciones artísticas, cada una con su propia historia única y atractivo visual. Explora la galería y encuentra inspiración en cada obra de arte."}
                </p>

                <a
                    href="#next-section"
                    className="mt-6 flex justify-center items-center text-gray-600 hover:text-gray-800 transition-all"
                    aria-label="Scroll down"
                >
                    <ChevronDown className="w-8 h-8" />
                </a>
            </div>

            {/* Floating Info Box - only shows when section is in view */}
            {showInfoBox && (
                <div className="hidden md:flex fixed bottom-6 right-6 bg-white bg-opacity-80 text-black px-4 py-3 rounded-xl shadow-xl z-40 items-center space-x-2 backdrop-blur-sm border border-gray-300">
                    <Info className="w-5 h-5 text-purple-500" />
                    <span className="text-sm font-medium">
                        {language === "English"
                            ? "Click an image to see more details"
                            : "Haz clic en una imagen para ver más detalles"}
                    </span>
                </div>
            )}

            {/* Modal */}
            {selectedImage && (
                <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex justify-center items-center px-4 animate-fadeIn">
                    <div className="bg-gradient-to-br from-[#c2b7f5] via-[#a0b9ff] to-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 relative">
                        {/* Close Button */}
                        <button
                            className="absolute top-4 right-4 text-red-500 hover:text-black transition"
                            onClick={() => setSelectedImage(null)}
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Image - no border now */}
                        <div className="w-full max-h-[60vh] overflow-hidden flex justify-center mb-4 p-4 rounded-lg">
                            <img
                                src={selectedImage.src}
                                alt={selectedImage.title}
                                className="object-contain max-h-[60vh] w-auto"
                            />
                        </div>

                        {/* Title & Description */}
                        <h2 className="text-2xl font-bold mb-2 text-center text-black drop-shadow-md">{selectedImage.title}</h2>
                        <p className="text-black text-center drop-shadow-sm">{selectedImage.description}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeaturedWork;
